import { Builder, DateSerializer, Field, From, inc, NullableDateSerializer, Optional, PrimaryKey, Schema } from '@bonusly/schema';

export enum EventFrequency {
	Biweekly = 'biweekly',
	Monthly = 'monthly',
	Weekly = 'weekly',
}

export enum EventOccurrence {
	First = 'first',
	Second = 'second',
	Third = 'third',
	Fourth = 'fourth',
	Last = 'last',
}

export enum MeetingStatus {
	Complete = 'complete',
	InProgress = 'in_progress',
	Scheduled = 'scheduled',
}

export enum MeetingTimelineStatus {
	Active = 'active',
	Past = 'past',
	Upcoming = 'upcoming',
}

@Schema()
export class EventEntity {
	@Field()
	@PrimaryKey()
	@From('id')
	id = '';

	@Field()
	@From('company_id')
	companyId = '';

	@Field()
	@From('frequency')
	frequency = EventFrequency.Weekly;

	@Field()
	@From('schedule_day_of_week')
	scheduleDayOfWeek = 1;

	@Field()
	@From('schedule_occurrence_in_month')
	scheduleOccurrenceInMonth: EventOccurrence | null = null;

	@Field()
	@From('shortcoded_url')
	shortcodeURL = '';

	@Field()
	@From('guest_id')
	guestId = '';

	@Field()
	@From('host_id')
	hostId = '';

	@Field()
	@From('manager_id')
	@Optional()
	managerId: string | null = null;

	@Field()
	@From('skip_manager_id')
	skipManagerId = false;

	@Field(DateSerializer)
	@From('canceled_at')
	canceledAt = new Date();

	@Field(DateSerializer)
	@From('created_at')
	createdAt = new Date();

	@Field(DateSerializer)
	@From('updated_at')
	updatedAt = new Date();

	@Field(NullableDateSerializer)
	@From('start_time')
	@Optional()
	startTime = new Date();

	@Field(NullableDateSerializer)
	@From('end_time')
	@Optional()
	endTime = new Date();

	@Field(DateSerializer)
	@From('next_meeting_date')
	@Optional()
	nextMeetingDate = new Date();

	@Field(DateSerializer)
	@From('last_meeting_start_time')
	@Optional()
	lastMeetingStartTime = new Date();

	@Field(DateSerializer)
	@From('last_meeting_end_time')
	@Optional()
	lastMeetingEndTime = new Date();

	@Field(NullableDateSerializer)
	@From('next_meeting_end_time')
	@Optional()
	nextMeetingEndTime: Date | null = null;

	@Field()
	@From('next_meeting_id')
	@Optional()
	nextMeetingId: string | null = null;

	@Field(NullableDateSerializer)
	@From('next_meeting_start_time')
	@Optional()
	nextMeetingStartTime: Date | null = null;

	@Field()
	@From('external_calendar_id')
	externalCalendarId: string | null = null;

	@Field()
	@From('external_calendar_link')
	externalCalendarLink: string | null = null;

	@Field()
	@From('external_calendar_provider')
	externalCalendarProvider: string | null = null;
}

@Schema()
export class TranscriptEntity {
	@Field()
	@From('id')
	id = '';

	@Field()
	@From('input_file_text')
	inputFileText = '';

	@Field()
	@From('extractions')
	extractions = {};
}

@Schema()
export class MeetingEntity {
	@Field()
	@PrimaryKey()
	@From('id')
	id = '';

	@Field()
	@From('company_id')
	companyId = '';

	@Field()
	@From('version_flag')
	versionFlag = 2;

	@Field()
	@From('event_id')
	eventId = '';

	@Field()
	@From('host_id')
	hostId = '';

	@Field()
	@From('guest_id')
	guestId = '';

	@Field()
	@From('status')
	status = MeetingStatus.Scheduled;

	@Field()
	@From('timeline_status')
	timelineStatus = MeetingTimelineStatus.Upcoming;

	@Field(DateSerializer)
	@From('created_at')
	createdAt = new Date();

	@Field(DateSerializer)
	@From('updated_at')
	updatedAt = new Date();

	@Field(NullableDateSerializer)
	@From('start_time')
	@Optional()
	startTime = new Date();

	@Field(NullableDateSerializer)
	@From('end_time')
	@Optional()
	endTime = new Date();
}

@Schema()
export class MeetingDetailEntity {
	@Field()
	@PrimaryKey()
	@From('id')
	id = '';

	@Field()
	@From('company_id')
	companyId = '';

	@Field()
	@From('version_flag')
	versionFlag = 2;

	@Field()
	@From('event_id')
	eventId = '';

	@Field()
	@From('host_id')
	hostId = '';

	@Field()
	@From('guest_id')
	guestId = '';

	@Field()
	@From('status')
	status = MeetingStatus.Scheduled;

	@Field()
	@From('timeline_status')
	timelineStatus = MeetingTimelineStatus.Upcoming;

	@Field(DateSerializer)
	@From('created_at')
	createdAt = new Date();

	@Field(DateSerializer)
	@From('updated_at')
	updatedAt = new Date();

	@Field(NullableDateSerializer)
	@From('start_time')
	@Optional()
	startTime = new Date();

	@Field(NullableDateSerializer)
	@From('end_time')
	@Optional()
	endTime = new Date();

	@Field()
	@From('sorted_agenda')
	@Optional()
	sortedAgenda: string[] = [];

	@Field()
	@From('summarized_agenda')
	@Optional()
	summarizedAgenda: any[] = [];

	@Field()
	@From('summarized_brief')
	@Optional()
	summarizedBrief = '';

	@Field()
	@From('edited_agenda')
	@Optional()
	editedAgenda = '';

	@Field()
	@From('edited_brief')
	@Optional()
	editedBrief = '';

	@Field()
	@From('transcript_user_id')
	@Optional()
	transcriptUserId = '';

	@Field()
	@From('event')
	event = new EventEntity();
}

export enum CalendarEventStatus {
	Confirmed = 'confirmed',
	Cancelled = 'cancelled',
}

export enum CalendarEventType {
	Default = 'default',
}

@Schema()
export class CalendarEventDateTimeEntity {
	@Field(DateSerializer)
	@From('dateTime')
	dateTime = new Date();
}

@Schema()
export class CalendarEventAttendeeEntity {
	@Field()
	@From('email')
	email = '';
}

@Schema()
export class CalendarEventEntity {
	@Field()
	@PrimaryKey()
	@From('id')
	id = '';

	@Field()
	@From('status')
	status: CalendarEventStatus | null = null;

	@Field()
	@From('eventType')
	eventType: CalendarEventType | undefined = undefined;

	@Field()
	@From('summary')
	summary = '';

	@Field()
	@From('htmlLink')
	htmlLink = '';

	@Field(CalendarEventDateTimeEntity)
	@From('start')
	start = new CalendarEventDateTimeEntity();

	@Field(CalendarEventDateTimeEntity)
	@From('end')
	end = new CalendarEventDateTimeEntity();

	@Field([CalendarEventAttendeeEntity])
	@From('attendees')
	attendees: CalendarEventAttendeeEntity[] = [];

	@Field([])
	@From('recurrence')
	recurrence: string[] = [];

	@Field()
	@From('recurringEventId')
	recurringEventId: string | null = null;
}

export const EventEntityBuilder = new Builder(EventEntity, {
	id: inc(String),
	companyId: '1',
	frequency: EventFrequency.Weekly,
	scheduleDayOfWeek: 1,
	scheduleOccurrenceInMonth: null,
	shortcodeURL: 'https://example.com/s/123',
	guestId: '1',
	hostId: '2',
	managerId: null,
	skipManagerId: false,
	canceledAt: () => new Date(),
	createdAt: () => new Date(),
	updatedAt: () => new Date(),
	startTime: () => new Date(),
	endTime: () => new Date(),
	nextMeetingDate: () => new Date(),
	lastMeetingStartTime: () => new Date(),
	lastMeetingEndTime: () => new Date(),
	nextMeetingEndTime: null,
	nextMeetingId: null,
	nextMeetingStartTime: null,
	externalCalendarId: null,
	externalCalendarLink: null,
	externalCalendarProvider: null,
});
